/**
 * The typed page context the assistant sends with every message (row B3). Replaces the
 * "[Context: …]" string the widget used to prepend. Only the shape is decided here; the
 * server validates it and resolves the site under the tenant scope.
 */
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSites } from '../../hooks/useSites';

export type AssistantPage = 'site' | 'charts' | 'historian' | 'alarms' | 'portfolio' | 'reports';
export interface AssistantContext { siteId?: string; page?: AssistantPage; queryId?: string }

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useAssistantContext(): { context: AssistantContext | undefined; siteName: string | null } {
  const location = useLocation();
  const { data: sites } = useSites();
  return useMemo(() => {
    const site = location.pathname.match(/^\/(project|charts)\/([^/]+)/);
    if (site) {
      // Only a UUID is sent: the server 400s anything else, which would fail every message.
      const siteId = UUID_RE.test(site[2]) ? site[2] : undefined;
      const page: AssistantPage = site[1] === 'project' ? 'site' : 'charts';
      // Tolerate a loading (undefined) or malformed payload: this renders app-wide.
      const list = Array.isArray(sites?.sites) ? sites.sites : [];
      const siteName = siteId ? (list.find((s) => s.site_id === siteId)?.name ?? null) : null;
      return { context: siteId ? { siteId, page } : { page }, siteName };
    }
    if (location.pathname.startsWith('/historian')) {
      const q = new URLSearchParams(location.search).get('q');
      return { context: q && UUID_RE.test(q) ? { page: 'historian' as const, queryId: q } : { page: 'historian' as const }, siteName: null };
    }
    if (location.pathname.startsWith('/alarms')) return { context: { page: 'alarms' as const }, siteName: null };
    if (location.pathname.startsWith('/dashboard')) return { context: { page: 'portfolio' as const }, siteName: null };
    if (location.pathname.startsWith('/reports')) return { context: { page: 'reports' as const }, siteName: null };
    return { context: undefined, siteName: null };
  }, [location.pathname, location.search, sites]);
}
